import React from 'react';

/**
 * Formats backend text into proper UI rendering.
 *
 * Handles:
 * - \n, \r\n, \r → line breaks
 * - \n\n → paragraph breaks
 * - Bullets: -, •, –, —
 * - Mixed content (paragraph + bullets)
 * - Extra spaces trimming
 * - Safe rendering (no HTML execution)
 */
export function formatTextContent(text: string | null | undefined): React.ReactNode {
    if (!text || !text.trim()) return null;

    const normalized = text.replace(/\r\n|\r/g, '\n').trim();

    const blocks = normalized.split(/\n{2,}/);

    return (
        <>
            {blocks.map((block, blockIndex) => {
                const rawLines = block.split('\n');

                // Keep structure but trim safely
                const lines = rawLines.map((l) => l.trim()).filter((l) => l.length > 0);

                if (lines.length === 0) return null;

                return (
                    <div key={blockIndex} style={{ marginBottom: '8px' }}>
                        {renderLines(lines)}
                    </div>
                );
            })}
        </>
    );
}

/**
 * Renders lines with proper handling of:
 * - Bullets
 * - Paragraphs
 * - Mixed content
 */
function renderLines(lines: string[]) {
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];

    const isBullet = (line: string) => /^([-•–—])\s+/.test(line); // supports -, •, –, —

    const cleanBullet = (line: string) => line.replace(/^([-•–—])\s+/, '');
    // const cleanBullet = (line: string) => line;

    lines.forEach((line, index) => {
        if (isBullet(line)) {
            currentList.push(cleanBullet(line));
        } else {
            // flush existing list before adding paragraph
            if (currentList.length > 0) {
                elements.push(
                    <ul key={`ul-${index}`} className="list-disc pl-5 mb-2">
                        {currentList.map((item, i) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ul>,
                );
                currentList = [];
            }

            elements.push(
                <p key={`p-${index}`} style={{ margin: '0 0 4px 0' }}>
                    {line}
                </p>,
            );
        }
    });

    // flush remaining list
    if (currentList.length > 0) {
        elements.push(
            <ul key="ul-last" className="list-disc pl-5 mb-2">
                {currentList.map((item, i) => (
                    <li key={i}>{item}</li>
                ))}
            </ul>,
        );
    }

    return elements;
}
